// Экран 6 — «История». Что ученик решал: все дни, новые сначала.
//
// Данные под него лежали с первого дня и никем не читались. `attempts` не
// чистится никогда по инварианту 2, и в журнале уже есть задача, навык, ввод,
// верность и время. Показывать это было нечему: «Прогресс» отвечает про
// состояние карты, «Итог дня» — про один день, а «что я вообще решал» не
// спрашивал никто.
//
// Чего здесь нет и не будет: ярлыка заблуждения. Точность классификации типа
// ошибки 40–47%, и досье прошлых ошибок ученику не показывается ни в секунду
// проверки, ни спустя месяц. Строка говорит, сошлось или нет; чем именно
// кончилось — раскрывается нажатием, задачей, а не диагнозом.
//
// Оценок и серий тут тоже нет. Список — это запись, а не табло: «14 из 20 за
// вчера» превращает историю в оценку дня, которая уже была показана один раз
// движением навыков и намеренно не повторяется числом.
//
// Порции приходят мутацией, а не подпиской, и причина не та, что на экране
// прогресса: там подписка запрещена инвариантом 4, здесь срез узкий. Причина
// в листании — страницы копятся, и живая последняя при снимках-предыдущих
// была бы хуже, чем снимок целиком. Разбор в docs/09-api.md.

import { useEffect, useRef, useState } from "react";
import { api, useMutation, useQuery, type HistoryPage, type HistoryRow } from "../api";
import { formatDay, plural } from "../lib/dates";
import { Formula } from "../lib/Formula";
import { byDay, duration, rowId, SOURCE_LABEL } from "../lib/history";
import { IconCheck, IconCross } from "../ui/Icons";
import { Solution } from "../ui/Solution";
import { ItemComments } from "../ui/Messages";

function Row({
  row,
  open,
  onToggle,
}: {
  row: HistoryRow;
  open: boolean;
  onToggle: () => void;
}) {
  const label = SOURCE_LABEL[row.source];
  const time = duration(row.ms);
  return (
    <button
      className={`pickrow hist__row ${open ? "pickrow--on" : ""}`}
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className={`hist__mark ${row.correct ? "hist__mark--grow" : "hist__mark--miss"}`}>
        {row.correct ? (
          <IconCheck size={12} color="#fff" />
        ) : (
          <IconCross size={12} color="#fff" />
        )}
      </span>
      <span className="hist__body">
        <span className="hist__statement">
          {row.statement === null ? (
            // Задача выпала из пула при пересборке банка. Попытка от этого не
            // перестала быть фактом, и прятать строку значило бы врать о том,
            // что ученик делал.
            <span className="faint">Задачи больше нет в пуле</span>
          ) : (
            <Formula source={row.statement} />
          )}
        </span>
        <span className="hist__meta">
          <span className="hist__skill">{row.skillName}</span>
          {time !== null ? <span className="faint">{time}</span> : null}
          {label !== null ? <span className="tag">{label}</span> : null}
        </span>
      </span>
    </button>
  );
}

/** Раскрытая строка: что ученик ответил, что было верно, разбор. */
function Review({ learnerKey, row }: { learnerKey: string; row: HistoryRow }) {
  const review = useQuery(api.history.review, {
    learnerKey,
    itemKey: row.itemKey,
    at: row.at,
  });

  if (review === undefined) {
    return (
      <div className="hist__open">
        <div className="skeleton" style={{ width: 240 }} />
      </div>
    );
  }
  if (review === null) {
    // Строки журнала нет — значит и раскрывать нечего. Достать эталон по
    // нерешённой задаче этим путём нельзя, и это не сообщение об ошибке.
    return (
      <div className="hist__open">
        <div className="faint">Записи по этой задаче нет.</div>
      </div>
    );
  }

  return (
    <div className="hist__open">
      <div className="hist__line">
        <span className="faint">Ответ:</span>{" "}
        <span className={review.correct ? "underline--grow" : "underline--miss"}>
          {review.raw === "" ? "пусто" : review.raw}
        </span>
      </div>

      {/* Эталон показывается только там, где он и так уже был показан один раз
          — в секунду проверки. На неразобранном вводе его нет и здесь. */}
      {!review.correct && review.answerDisp !== null ? (
        <div className="hist__line">
          <span className="faint">Верно:</span>{" "}
          <span className="underline--grow">
            <Formula source={review.answerDisp} />
          </span>
        </div>
      ) : null}

      {/* Разбор есть и у верно решённой задачи, и открыт сразу — как на
          экране решения. Разметка одна на все экраны: `src/ui/Solution.tsx`. */}
      <Solution
        steps={review.solution}
        unclear={{ learnerKey, itemKey: row.itemKey, where: "История" }}
      />

      {review.statement === null ? (
        <div className="faint" style={{ marginTop: 10 }}>
          Разбор недоступен: задача выпала из пула. Ответ и вердикт — из журнала.
        </div>
      ) : null}

      {/* Что взрослый написал к этой задаче — под разбором, где её и читают. */}
      <ItemComments learnerKey={learnerKey} itemKey={row.itemKey} />
    </div>
  );
}

export function History({
  learnerKey,
  onToday,
  onProgress,
}: {
  learnerKey: string;
  onToday: () => void;
  onProgress: () => void;
}) {
  const page = useMutation(api.history.page);
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const asked = useRef(false);

  /** `before === null` — первая порция; она же заменяет список, а не дополняет. */
  const load = (before: number | null) => {
    setFailed(null);
    setLoading(true);
    void page(before === null ? { learnerKey } : { learnerKey, before })
      .then((data: HistoryPage) => {
        setRows((prev) => (before === null || prev === null ? data.rows : [...prev, ...data.rows]));
        setNext(data.nextBefore);
      })
      .catch((e: unknown) => setFailed(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (asked.current) return;
    // Ref, а не пустой список зависимостей: StrictMode прогоняет эффект
    // дважды, и первая порция запрашивалась бы два раза на каждое открытие.
    asked.current = true;
    load(null);
  }, []);

  if (failed !== null) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <h1 className="h1">История не собралась</h1>
          <p className="lede">{failed}</p>
          <div className="row row--actions" style={{ marginTop: 16 }}>
            <button className="btn btn--primary" onClick={() => load(null)}>
              Ещё раз
            </button>
            <button className="link link--quiet" onClick={onToday}>
              К набору дня
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (rows === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          {/* Имя экрана — в шапке приложения; здесь только его состояние. */}
          <h1 className="h1">Пока пусто</h1>
          <p className="lede">
            Здесь появятся все решённые задачи — с ответом, вердиктом и разбором.
          </p>
          <div className="row row--actions" style={{ marginTop: 16 }}>
            <button className="btn btn--primary btn--big" onClick={onToday}>
              К набору дня
            </button>
          </div>
        </div>
      </div>
    );
  }

  const days = byDay(rows);

  return (
    <div className="cols cols--solo">
      <div className="col">
        <div>
          {/* Надзаголовка «История» здесь нет: имя экрана стоит в шапке
              приложения. Заголовок ниже считает, а не называет. */}
          {/*
            Заголовок считает показанное, а не всё решённое: «всего» стоило бы
            обхода journal целиком на каждое открытие, а к июню 2027 это 4
            тысячи строк. Число под кнопкой честнее круглого итога, который
            пришлось бы обновлять отдельным счётчиком.
          */}
          <h1 className="h1">
            Показано <span className="num">{rows.length}</span>{" "}
            {plural(rows.length, "задача", "задачи", "задач")}
          </h1>
        </div>

        {days.map((day) => (
          <div className="card" key={day.date}>
            <div className="card__label">{formatDay(day.date)}</div>
            <div className="hist" style={{ marginTop: 10 }}>
              {day.rows.map((row) => {
                const id = rowId(row);
                return (
                  <div key={id}>
                    <Row
                      row={row}
                      open={open === id}
                      onToggle={() => setOpen((prev) => (prev === id ? null : id))}
                    />
                    {open === id ? <Review learnerKey={learnerKey} row={row} /> : null}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="row row--actions">
          {next !== null ? (
            <button
              className="btn btn--outline"
              disabled={loading}
              onClick={() => load(next)}
            >
              {loading ? "Загружается…" : "Ещё"}
            </button>
          ) : (
            <span className="faint">Это всё.</span>
          )}
          <button className="link link--quiet" onClick={onProgress}>
            Прогресс
          </button>
        </div>
      </div>
    </div>
  );
}
